import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Copy,
  ExternalLink,
  MoreHorizontal,
  Plus,
  QrCode,
  Search,
  Trash2,
  Power,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers", { params: { search } });
      setCustomers(data.customers || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const filtered = useMemo(() => customers, [customers]);

  const copyLink = async (c) => {
    try {
      await navigator.clipboard.writeText(c.nfc_link);
      toast.success("NFC-Link kopiert.");
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const toggleStatus = async (c) => {
    setBusy((b) => ({ ...b, [c.id]: true }));
    try {
      await api.patch(`/customers/${c.id}`, {
        status: c.status === "active" ? "inactive" : "active",
      });
      toast.success(c.status === "active" ? "Deaktiviert." : "Aktiviert.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [c.id]: false }));
    }
  };

  const removeCustomer = async (c) => {
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Kunde gelöscht.");
      setCustomers((cs) => cs.filter((x) => x.id !== c.id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="label-eyebrow">Kunden</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">
            Alle Kunden
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Verwalte alle Firmen und ihre Bewertungslinks.
          </p>
        </div>
        <Link to="/kunden/neu">
          <Button
            className="rounded-full h-11 px-5 gap-2"
            data-testid="customers-new-customer-btn"
          >
            <Plus className="w-4 h-4" /> Neue Firma
          </Button>
        </Link>
      </header>

      <div className="card-surface p-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="customers-search-input"
            placeholder="Firma, Adresse oder Slug suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-11 bg-background/50"
          />
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block card-surface overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/[0.06] hover:bg-transparent">
              <TableHead className="label-eyebrow">Firma</TableHead>
              <TableHead className="label-eyebrow">Stand-URL</TableHead>
              <TableHead className="label-eyebrow">Status</TableHead>
              <TableHead className="label-eyebrow text-right">Scans</TableHead>
              <TableHead className="label-eyebrow text-right">Heute</TableHead>
              <TableHead className="label-eyebrow">Erstellt</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="py-16 text-center">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                  Lädt…
                </TableCell>
              </TableRow>
            ) : filtered.length ? (
              filtered.map((c) => (
                <TableRow
                  key={c.id}
                  data-testid={`customer-row-${c.id}`}
                  className="border-white/[0.05] hover:bg-white/[0.02]"
                >
                  <TableCell className="align-middle">
                    <Link
                      to={`/kunden/${c.id}`}
                      className="font-medium hover:text-primary"
                    >
                      {c.business_name}
                    </Link>
                    <div className="text-xs text-muted-foreground truncate max-w-[280px]">
                      {c.address}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground font-mono">
                    /t/{c.stand_code || "—"}
                  </TableCell>
                  <TableCell>
                    <LifecycleBadge status={c.lifecycle_status} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{c.total_scans}</TableCell>
                  <TableCell className="text-right tabular-nums text-primary">
                    {c.scans_today}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {fmtDate(c.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <RowActions
                      customer={c}
                      onCopy={copyLink}
                      onToggle={toggleStatus}
                      onDelete={removeCustomer}
                      busy={busy[c.id]}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="py-16 text-center text-muted-foreground">
                  Noch keine Kunden. Klicke oben auf „Neue Firma“.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && (
          <div className="card-surface p-6 text-center text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Lädt…
          </div>
        )}
        {!loading &&
          filtered.map((c) => (
            <div key={c.id} className="card-surface p-4" data-testid={`customer-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link to={`/kunden/${c.id}`} className="font-medium hover:text-primary">
                    {c.business_name}
                  </Link>
                  <div className="text-xs text-muted-foreground truncate">{c.address}</div>
                  <div className="text-[11px] text-muted-foreground mt-1 truncate font-mono">
                    /t/{c.stand_code || "—"}
                  </div>
                </div>
                <LifecycleBadge status={c.lifecycle_status} />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                <Stat label="Scans" value={c.total_scans} />
                <Stat label="Heute" value={c.scans_today} tone="primary" />
                <Stat label="Erstellt" value={fmtDate(c.created_at)} small />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => copyLink(c)}
                  className="rounded-full gap-1.5"
                  data-testid={`mobile-copy-${c.id}`}
                >
                  <Copy className="w-3.5 h-3.5" /> Link
                </Button>
                <Link to={`/kunden/${c.id}`}>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="rounded-full gap-1.5"
                    data-testid={`mobile-open-${c.id}`}
                  >
                    <QrCode className="w-3.5 h-3.5" /> QR & Details
                  </Button>
                </Link>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => toggleStatus(c)}
                  className="rounded-full gap-1.5"
                  disabled={busy[c.id]}
                  data-testid={`mobile-toggle-${c.id}`}
                >
                  <Power className="w-3.5 h-3.5" />
                  {c.status === "active" ? "Deaktivieren" : "Aktivieren"}
                </Button>
              </div>
            </div>
          ))}
        {!loading && !filtered.length && (
          <div className="card-surface p-8 text-center text-sm text-muted-foreground">
            Noch keine Kunden.
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone, small }) {
  return (
    <div className="rounded-lg bg-white/[0.02] py-2">
      <div
        className={`font-heading font-semibold ${small ? "text-xs" : "text-base"} ${
          tone === "primary" ? "text-primary" : ""
        }`}
      >
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

const LIFECYCLE_LABELS = {
  new: { label: "Neu", cls: "border-white/10 text-muted-foreground bg-white/[0.03]" },
  preparing: { label: "In Vorbereitung", cls: "border-amber-500/30 text-amber-400 bg-amber-500/10" },
  ready: { label: "Verkaufsbereit", cls: "border-primary/30 text-primary bg-primary/10" },
  delivered: { label: "Ausgeliefert", cls: "border-emerald-500/30 text-emerald-400 bg-emerald-500/10" },
};

function LifecycleBadge({ status }) {
  const l = LIFECYCLE_LABELS[status] || LIFECYCLE_LABELS.new;
  return (
    <Badge variant="outline" className={l.cls} data-testid={`lifecycle-${status || "new"}`}>
      {l.label}
    </Badge>
  );
}


function RowActions({ customer, onCopy, onToggle, onDelete, busy }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          data-testid={`row-actions-${customer.id}`}
          disabled={busy}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreHorizontal className="w-4 h-4" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-popover">
        <DropdownMenuItem asChild data-testid={`action-open-${customer.id}`}>
          <Link to={`/kunden/${customer.id}`}>
            <ExternalLink className="w-4 h-4 mr-2" /> Öffnen
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onCopy(customer)}
          data-testid={`action-copy-${customer.id}`}
        >
          <Copy className="w-4 h-4 mr-2" /> Link kopieren
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid={`action-qr-${customer.id}`}>
          <Link to={`/kunden/${customer.id}#qr`}>
            <QrCode className="w-4 h-4 mr-2" /> QR-Code
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onToggle(customer)}
          data-testid={`action-toggle-${customer.id}`}
        >
          <Power className="w-4 h-4 mr-2" />
          {customer.status === "active" ? "Deaktivieren" : "Aktivieren"}
        </DropdownMenuItem>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <DropdownMenuItem
              onSelect={(e) => e.preventDefault()}
              className="text-destructive focus:text-destructive"
              data-testid={`action-delete-${customer.id}`}
            >
              <Trash2 className="w-4 h-4 mr-2" /> Löschen
            </DropdownMenuItem>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Kunde wirklich löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Diese Aktion entfernt <strong>{customer.business_name}</strong> und alle
                zugehörigen Scans dauerhaft.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDelete(customer)}
                className="bg-destructive hover:bg-destructive/90"
                data-testid={`confirm-delete-${customer.id}`}
              >
                Endgültig löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
